import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Check,
  ChevronRight,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  RefreshCw,
  Sparkles,
  X,
  AlertTriangle,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/liquid-rhinoplasty-mumbai";
const RHINO_SURGICAL_PATH = "/rhinoplasty-in-mumbai";
const FILLERS_PATH = "/dermal-fillers-mumbai";
const CHIN_PATH = "/chin-augmentation-in-mumbai";
const NON_SURGICAL_PATH = "/non-surgical-treatments-in-mumbai";

const LIQUID_RHINO_FAQS = [
  {
    question: "Is liquid rhinoplasty safe?",
    answer:
      "It is safe when performed by an injector with specific knowledge of nasal vascular anatomy and the precautions required for this high-risk injection area. The nose is connected to the ophthalmic artery through the angular artery, and vascular occlusion from nasal filler is a rare but potentially sight-threatening complication. Safe nasal filler injection requires cannula technique where possible, slow low-pressure injection in small increments, use of only hyaluronic acid filler, and hyaluronidase immediately available for emergency dissolution. These requirements make injector selection the most important safety decision in liquid rhinoplasty.",
  },
  {
    question: "Can liquid rhinoplasty make my nose smaller?",
    answer:
      "No. Liquid rhinoplasty adds volume. It cannot reduce the size, width, or overall projection of a large nose. It can create the visual impression of a more balanced nose by improving specific proportional relationships, such as making a hump less prominent by adding filler above and below it, but the nose itself does not become physically smaller. Patients who want size reduction require surgical rhinoplasty.",
  },
  {
    question: "How long does liquid rhinoplasty last?",
    answer:
      "Typically 9 to 18 months depending on the filler type used, the amount injected, and the patient's metabolic rate. The nose is a relatively low-movement area compared to the lips, so nasal filler tends to persist toward the longer end of this range. Maintenance sessions every 12 to 18 months sustain the result.",
  },
  {
    question: "Can liquid rhinoplasty be reversed?",
    answer:
      "Yes, when hyaluronic acid filler is used. Hyaluronidase enzyme dissolves hyaluronic acid filler rapidly, typically within 24 to 48 hours, and restores the pre-treatment baseline. This reversibility is one of the primary safety advantages of liquid rhinoplasty over surgery. Dr. Jain uses only hyaluronic acid filler for nasal injection precisely because of this reversibility.",
  },
  {
    question: "Will liquid rhinoplasty affect my breathing?",
    answer:
      "No. Filler placed in the external nasal tissue does not enter the nasal airway and does not affect breathing mechanics. However, liquid rhinoplasty also cannot improve breathing problems. Nasal obstruction from structural causes requires surgical correction of the internal nasal anatomy, which filler cannot address.",
  },
  {
    question: "Can I have liquid rhinoplasty if I have had surgical rhinoplasty?",
    answer:
      "Sometimes, but with additional caution. Previous surgical rhinoplasty alters the nasal vasculature and may make certain areas more or less appropriate for filler injection depending on what was done surgically and how the tissue healed. Patients with prior nasal surgery must inform Dr. Jain of the full surgical history before liquid rhinoplasty is considered. Scar tissue from surgery changes the anatomical landmarks used for safe injection, and a careful assessment determines whether filler is appropriate and which areas can be safely treated.",
  },
  {
    question: "Can liquid rhinoplasty be combined with other treatments?",
    answer:
      "Yes. Chin filler is frequently combined with nasal filler because the nose-to-chin relationship determines profile balance, and improving both together often produces a more natural overall facial result than treating the nose alone. Jawline contouring alongside nasal and chin filler addresses the full facial profile as a coordinated plan.",
  },
  {
    question: "What happens if I decide to have surgical rhinoplasty after liquid rhinoplasty?",
    answer:
      "Any remaining filler should be fully dissolved with hyaluronidase before surgical rhinoplasty is performed. Residual filler in the nasal tissue can distort the surgeon's assessment of the nasal anatomy and interfere with the surgical reshaping of the underlying bone and cartilage. Dr. Jain dissolves nasal filler well in advance of any planned surgical rhinoplasty and allows time for the tissue to return to its natural state before surgery.",
  },
];

const noseBodyLocation = { "@type": "AnatomicalStructure", name: "Nose" };
const plasticSpecialty = "https://schema.org/PlasticSurgery";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Liquid rhinoplasty (non-surgical nose job)",
      description:
        "Minimally invasive hyaluronic acid filler injection to refine nasal contour, camouflage irregularities, and balance profile; not a substitute for surgical reduction or airway surgery.",
      procedureType: "Minimally invasive",
      bodyLocation: noseBodyLocation,
      medicalSpecialty: plasticSpecialty,
      contraindication:
        "Not appropriate as primary treatment for nasal size reduction, nasal airway obstruction or breathing correction, or major structural deformity—these goals require surgical rhinoplasty or septal surgery.",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: LIQUID_RHINO_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function LiquidRhinoplastyJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical Treatments", href: NON_SURGICAL_PATH },
    { name: "Liquid Rhinoplasty", href: "/liquid-rhinoplasty-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-12 pb-16 md:pb-24" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-medium tracking-widest text-gold uppercase mb-4">Non-Surgical</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-premium-purple leading-[1.15] mb-6">Liquid Rhinoplasty</h1>
            <p className="text-lg text-deep-navy/70 mb-8">
              Non-Surgical Nose Reshaping with Fillers by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">Board Certified</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Liquid Rhinoplasty" icon="👃" size="hero"  imageSrc={HERO.liquidRhinoplasty} imageAlt="Liquid rhinoplasty"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Liquid Rhinoplasty?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Liquid rhinoplasty, also called a non-surgical nose job, is a cosmetic procedure that uses hyaluronic acid dermal filler injected into specific points on the nose to reshape its contour, camouflage irregularities, and improve its appearance within the face. The procedure takes 15 to 30 minutes, produces results immediately, and requires no surgery, no anesthesia, and no recovery period.
            </p>
            <p>
              Liquid rhinoplasty works by addition, not subtraction. It cannot make the nose smaller. It reshapes the nose's appearance by adding volume strategically so that irregularities, humps, and asymmetries are visually balanced. A dorsal hump is not removed but its visual prominence is reduced by filling the area above and below it to create a straighter nasal profile. A flat bridge is augmented to add definition. A drooping tip is given the appearance of a slight lift by adding support at the supratip.
            </p>
            <p>
              Dr. Vicky Jain performs liquid rhinoplasty as an alternative for patients with mild to moderate nasal concerns who want to assess a change before committing to surgery, or who want a temporary, reversible improvement. Patients with concerns requiring structural correction, size reduction, or breathing improvement are advised that surgical rhinoplasty is the appropriate treatment and that liquid rhinoplasty will not address their concern.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Dr. Jain&apos;s Approach to Liquid Rhinoplasty</h2>
            <div className="space-y-6 text-deep-navy/70 leading-relaxed">
              <p>
                The nose is the highest-risk area on the face for vascular complications from filler injection. The nasal blood supply is connected to the ophthalmic artery through the angular artery pathway. Filler injected into the nasal tip, dorsum, or sidewall under excess pressure can enter or compress the angular artery and cause occlusion that propagates to the ophthalmic circulation, with the potential to cause retinal artery occlusion and vision loss. This is a rare but documented complication of nasal filler that is irreversible. Dr. Jain uses cannula technique for nasal filler injection wherever anatomically feasible, injects slowly with low pressure, uses only hyaluronic acid fillers that can be dissolved with hyaluronidase, and maintains hyaluronidase in the treatment room at every nasal filler session. These are non-negotiable safety requirements, not optional precautions.
              </p>
              <p>
                Aesthetic planning for liquid rhinoplasty starts with the entire face, not just the nose. The nose must be assessed in relation to the chin, forehead, and facial proportions. A nasal change that improves the nose viewed in isolation may not improve the face as a whole if the chin is recessed or the mid-face is disproportionate. Dr. Jain frequently discusses chin filler alongside nasal filler because the nose-to-chin relationship is the primary determinant of profile balance, and a combined approach often produces a more natural facial result than nasal filler alone.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #FAFAF8 0%, rgba(250, 250, 248, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-premium-purple">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="safety-section" aria-labelledby="safety-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 id="safety-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center max-w-4xl mx-auto">
          Safety in Liquid Rhinoplasty: Why the Nose is a High-Risk Area
        </h2>
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-4xl mx-auto py-6 px-4 md:px-6">
          <AlertTriangle className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle className="font-serif text-lg text-premium-purple pl-7 mb-4">Critical safety information</AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-6">
            <p className="leading-relaxed">
              Nasal filler injection carries a higher vascular complication risk than filler in most other facial areas because the nasal blood supply connects to the ophthalmic artery through the angular artery. Filler entering this arterial pathway can cause vascular occlusion that travels to the retinal artery, with the potential for permanent vision loss. This is a rare but documented complication in the medical literature with cases reported globally.
            </p>
            <p className="leading-relaxed">
              The risk is minimized but not eliminated by expert technique. The precautions that meaningfully reduce risk include using a blunt cannula rather than a sharp needle wherever the anatomy allows, injecting in small increments with low pressure and pausing between increments, using only hyaluronic acid filler that is reversible with hyaluronidase enzyme, having hyaluronidase immediately available for emergency dissolution, and knowing the specific injection points to avoid. These precautions require detailed knowledge of facial anatomy. They cannot be applied by an injector without this knowledge. Choosing an injector with specific training in facial anatomy and vascular risk management in the nose is not a preference. It is the single most important safety decision a patient makes before nasal filler treatment.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

const CAN_ITEMS = [
  {
    title: "Camouflage a dorsal hump",
    text: "Adding filler above and below the hump creates a straighter nasal profile in photographs and from the side view without removing any tissue.",
  },
  {
    title: "Add height to a flat nasal bridge",
    text: "Patients who want a higher, more defined nasal bridge from the front and side view benefit from filler placed along the dorsum.",
  },
  {
    title: "Improve the appearance of a drooping tip",
    text: "Adding support at the supratip and columella creates the visual impression of a slightly lifted tip without structural change.",
  },
  {
    title: "Correct minor asymmetry",
    text: "Small volume differences between the two sides of the nose can be balanced with targeted filler placement.",
  },
  {
    title: "Preview surgical rhinoplasty",
    text: "Patients considering surgical rhinoplasty can use liquid rhinoplasty to assess whether a change they are considering improves their face before committing to a permanent procedure.",
  },
];

const CANNOT_ITEMS = [
  {
    title: "Reduce nose size",
    text: "Filler adds volume. It cannot reduce the overall size, width, or projection of a large nose. Surgery is the only way to make a nose smaller.",
  },
  {
    title: "Fix breathing problems",
    text: "Nasal obstruction from a deviated septum, inferior turbinate hypertrophy, or nasal valve collapse requires surgical correction. Filler does not address any structural airway problem.",
  },
  {
    title: "Correct significant structural deformity",
    text: "Post-traumatic deformity, severe deviation, or congenital structural abnormality requires surgical rhinoplasty for meaningful correction.",
  },
  {
    title: "Produce permanent results",
    text: "All hyaluronic acid filler results are temporary, lasting 9 to 18 months. Patients who want a permanent result need surgical rhinoplasty.",
  },
];

function CanCannotSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="can-cannot-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Liquid Rhinoplasty Can and Cannot Achieve</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-6">What liquid rhinoplasty can do</h3>
            <ul className="space-y-5">
              {CAN_ITEMS.map((item) => (
                <li key={item.title} className="flex items-start gap-3 text-deep-navy/70 leading-relaxed">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <span className="font-medium text-deep-navy">{item.title}: </span>
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-6">What liquid rhinoplasty cannot do</h3>
            <ul className="space-y-5">
              {CANNOT_ITEMS.map((item) => (
                <li key={item.title} className="flex items-start gap-3 text-deep-navy/70 leading-relaxed">
                  <X className="w-5 h-5 text-deep-navy/40 flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <span className="font-medium text-deep-navy">{item.title}: </span>
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function GoodCandidateSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="good-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Is a Good Candidate for Liquid Rhinoplasty?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates want to improve a specific aspect of their nose's appearance, have mild to moderate concerns, and have realistic expectations about the temporary and additive nature of the procedure. Patients who want to preview what a dorsal hump reduction or tip refinement would look like before deciding on surgery are ideal candidates. Patients who want subtle improvement without the recovery, cost, or permanence of surgery are also well-suited to liquid rhinoplasty.
            </p>
            <p>
              Patients who are not appropriate candidates include those who want size reduction, those with breathing problems, those with previous nasal filler that has not dissolved and is distorting the anatomy, and those with a history of vascular conditions affecting the facial vasculature. Patients who have had previous surgical rhinoplasty should inform Dr. Jain before liquid rhinoplasty as prior surgery alters the nasal vasculature and changes the risk profile of subsequent filler injection. Dr. Jain advises directly when a patient's concern requires surgical rhinoplasty and is not addressable with fillers, rather than providing a temporary treatment for a problem that needs a structural solution.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_SURGICAL_ROWS = [
  {
    label: "What It Can Achieve",
    liquid:
      "Camouflage irregularities, add bridge height, improve tip appearance. Cannot reduce size or fix breathing.",
    surgical:
      "Reduce or augment size, reshape bone and cartilage, correct breathing, produce permanent structural change.",
  },
  {
    label: "Permanence",
    liquid: "Temporary. Results last 9 to 18 months.",
    surgical: "Permanent.",
  },
  {
    label: "Downtime",
    liquid: "None. Normal activity the same day.",
    surgical: "1 to 2 weeks with splint. Final result at 12 months.",
  },
  {
    label: "Reversibility",
    liquid: "Yes. Hyaluronic acid filler dissolved with hyaluronidase.",
    surgical: "No. Structural changes are permanent.",
  },
  {
    label: "Appropriate For",
    liquid: "Minor irregularities, hump camouflage, bridge height, asymmetry correction, surgical preview.",
    surgical: "Size reduction, breathing correction, significant structural correction, permanent change.",
  },
  {
    label: "Cost",
    liquid: "Lower initial cost. Repeated every 9 to 18 months.",
    surgical: "Higher upfront cost. One-time investment for permanent results.",
  },
];

function VsSurgicalSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="vs-surgical-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Liquid Rhinoplasty vs. Surgical Rhinoplasty</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Liquid Rhinoplasty
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  <Link to={RHINO_SURGICAL_PATH} className="hover:text-gold transition-colors">
                    Surgical Rhinoplasty
                  </Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_SURGICAL_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.liquid}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.surgical}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          For patients who are uncertain whether their concern requires surgery or whether liquid rhinoplasty will suffice, a consultation with a surgeon who performs both procedures is the most informative approach. Dr. Jain performs both surgical and non-surgical rhinoplasty and advises on which is appropriate for each patient's specific anatomy and goals without a preference toward one approach over the other.
        </p>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Liquid Rhinoplasty is Performed</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure takes 15 to 30 minutes. A topical numbing cream is applied to the nose for 15 to 20 minutes before injection. The skin is cleaned and the planned injection points are marked based on the facial assessment and the specific correction being made.
            </p>
            <p>
              Dr. Jain uses a blunt-tipped cannula for the majority of nasal filler placement to minimize vascular risk. The cannula is passed through a small entry point and guided to the planned injection plane. Filler is deposited in small increments with pauses between deposits to assess the evolving result and allow time to observe any adverse tissue response. Sharp needles are used only where anatomically required and with additional precautions at those specific points.
            </p>
            <p>
              After injection, the nose is gently assessed visually and the filler is molded to ensure even distribution. Ice is applied briefly. Patients leave the clinic immediately. Instructions are provided for the first 24 to 48 hours including avoiding glasses pressure on the nose, not touching or pressing the nose, and avoiding strenuous exercise.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultsTimelineSection() {
  const milestones = [
    {
      period: "Immediately After",
      description:
        "Visible reshaping is present immediately after injection. Some swelling from the injection may slightly overestimate the final result.",
      icon: Sparkles,
    },
    {
      period: "Days 2 to 3",
      description: "Swelling resolves. The final result is visible and represents the expected outcome. Any bruising from injection is settling.",
      icon: Monitor,
    },
    {
      period: "Weeks 1 to 2",
      description:
        "The filler has fully integrated into the nasal tissue. The result is stable and will remain consistent until the filler begins to slowly dissolve.",
      icon: Clock,
    },
    {
      period: "Months 9 to 18",
      description:
        "Results gradually reduce as the hyaluronic acid is naturally metabolized. Maintenance treatment sustains the improvement. Patients who decide to proceed to surgical rhinoplasty can have any remaining filler dissolved before surgery.",
      icon: RefreshCw,
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="results-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-12 text-center">Results Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Rhinoplasty" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {LIQUID_RHINO_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`liquid-rhino-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Liquid Rhinoplasty Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your nasal structure and facial proportions to determine whether liquid rhinoplasty addresses your specific concern or whether surgical rhinoplasty is the more appropriate treatment. The safety protocol for nasal filler injection is explained in detail. If liquid rhinoplasty is appropriate, the planned correction is discussed before any injection is placed. You leave with a clear understanding of what is achievable and what the procedure involves.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Rhinoplasty",
      description: "Permanent surgical nose reshaping for patients wanting size reduction, structural correction, or breathing improvement.",
      href: RHINO_SURGICAL_PATH,
    },
    {
      name: "Dermal Fillers",
      description: "Facial volume and contouring for patients addressing multiple areas alongside nasal reshaping.",
      href: FILLERS_PATH,
    },
    {
      name: "Chin Augmentation",
      description: "Chin enhancement to complement nasal reshaping for complete facial profile balance.",
      href: CHIN_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LiquidRhinoplastyPage() {
  return (
    <>
      <SEO
        title="Liquid Rhinoplasty | Non-Surgical Nose Job by Dr. Vicky Jain"
        description="Reshape your nose without surgery using dermal fillers. Dr. Vicky Jain performs liquid rhinoplasty with instant, reversible results. Book your consultation."
        canonical="/liquid-rhinoplasty-mumbai"
      />
      <LiquidRhinoplastyJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <SafetySection />
      <CanCannotSection />
      <GoodCandidateSection />
      <VsSurgicalSection />
      <HowPerformedSection />
      <ResultsTimelineSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
